/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Injectable, inject } from '@angular/core';
import { OSKTranslateService } from '@oskey/translate';
import { OSKToastService } from '@oskey/ui';

@Injectable({
  providedIn: 'root'
})
export class OSKErrorService {
  private readonly toast: OSKToastService = inject(OSKToastService);
  private readonly translate: OSKTranslateService | null = inject(OSKTranslateService, {
    optional: true
  });
  private readonly defaultErrorKey: string = 'errors.default';
  private lastErrorSignature = '';
  private lastErrorTimestamp = 0;

  /**
   * Shows a toast notification with a specific error message.
   * If a translation key is not provided or does not match a known translation key,
   * the default error message ('errors.default') is shown along with any readable error detail.
   *
   * @param { string | undefined } msgTranslationKey The translation key or raw error message.
   */
  showError(msgTranslationKey?: string): void {
    const defaultTranslated = this.translate?.instant(this.defaultErrorKey);
    const defaultMsg =
      typeof defaultTranslated === 'string' && defaultTranslated !== this.defaultErrorKey
        ? defaultTranslated
        : 'Something went wrong. Please try again.';

    let title = defaultMsg;

    if (msgTranslationKey && typeof msgTranslationKey === 'string') {
      const translated = this.translate?.instant(msgTranslationKey);
      if (typeof translated === 'string' && translated && translated !== msgTranslationKey) {
        title = translated;
      }
    }

    const now = Date.now();
    const signature = title;
    if (signature === this.lastErrorSignature && now - this.lastErrorTimestamp < 1000) {
      return;
    }
    this.lastErrorSignature = signature;
    this.lastErrorTimestamp = now;

    this.toast.danger(title);
  }
}
